import React, { useMemo, useState } from 'react';
import { Loader, Tag, X } from 'lucide-react';
import { CardContent } from '@/components/ui/Card.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import PrimitiveInput from '@/components/form/_primitives/PrimitiveInput.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import { AppliedDiscountDto } from '@/features/public/booking/types/AppliedDiscountDto.ts';
import { getDiscountMechanicLabel } from '@/features/public/booking/types/AppliedDiscountDto.utils.ts';
import { PromoCodeStatusDto } from '@/features/public/booking/types/BookingQuoteDto.ts';
import { PromoCodeStatus } from '@/features/public/booking/types/PromoCodeStatus.ts';
import {
  getPromoCodeStatusLabel,
  promoCodeStatusIcons,
  promoCodeStatusTones,
} from '@/features/public/booking/types/PromoCodeStatus.utils.ts';
import StatusDot from '@/components/ui/StatusDot.tsx';
import {
  draftOf,
  useBookingStore,
} from '@/features/public/booking/context/bookingStore.tsx';
import { cn } from '@/lib/utils.ts';

const MIN_CODE_LENGTH = 3;
const MAX_CODE_LENGTH = 100;

/**
 * Pojedynczy wiersz listy rabatów:
 * - 'code'      - kod wpisany przez klienta (usuwalny); status z quote.promoCodeStatuses,
 * - 'automatic' - rabat automatyczny naliczony przez backend (bez kodu, nieusuwalny).
 */
type DiscountRow =
  | {
      kind: 'code';
      code: string;
      status?: PromoCodeStatus;
      discount?: AppliedDiscountDto;
    }
  | { kind: 'automatic'; discount: AppliedDiscountDto };

const DiscountRowItem = ({
  row,
  isRecalculating,
  onRemove,
}: {
  row: DiscountRow;
  isRecalculating: boolean;
  onRemove: (code: string) => void;
}) => {
  const discount = row.discount;
  const isApplied = !!discount;
  const isDeferred =
    row.kind === 'code' && row.status === PromoCodeStatus.DEFERRED;
  // Niezastosowanego kodu nie oznaczamy dopóki trwa przeliczanie - dane są nieaktualne.
  const isNotApplied =
    row.kind === 'code' && !discount && !isDeferred && !isRecalculating;

  const label =
    row.kind === 'code'
      ? row.code
      : (discount!.definition.promoCode ?? discount!.name);
  const mechanicLabel = discount
    ? getDiscountMechanicLabel(discount.definition)
    : null;

  // Rabat automatyczny i kod w trakcie przeliczania nie mają jeszcze losu -
  // znacznik zostaje wtedy neutralny.
  const rowStatus = isApplied
    ? PromoCodeStatus.APPLIED
    : isDeferred
      ? PromoCodeStatus.DEFERRED
      : isNotApplied
        ? PromoCodeStatus.NOT_APPLIED
        : null;

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm',
        isNotApplied
          ? 'bg-error-50 text-error-300'
          : 'bg-neutral-50 text-base-text',
      )}
    >
      <div className="flex min-w-0 flex-row items-center gap-1.5">
        <StatusDot
          icon={rowStatus ? promoCodeStatusIcons[rowStatus] : Tag}
          tone={rowStatus ? promoCodeStatusTones[rowStatus] : 'neutral'}
          label={rowStatus ? getPromoCodeStatusLabel(rowStatus) : ''}
          showLabel={false}
        />
        <span className="truncate">
          {label}
          {mechanicLabel && (
            <span className="text-neutral-400"> ({mechanicLabel})</span>
          )}
        </span>
        {row.kind === 'automatic' && (
          <span className="shrink-0 text-xs text-neutral-400">
            automatyczny
          </span>
        )}
        {isDeferred && !isApplied && (
          <span className="shrink-0 text-xs text-neutral-400">
            {getPromoCodeStatusLabel(PromoCodeStatus.DEFERRED)}
          </span>
        )}
        {isNotApplied && (
          <span className="shrink-0 text-xs">
            {getPromoCodeStatusLabel(PromoCodeStatus.NOT_APPLIED)}
          </span>
        )}
      </div>

      <div className="flex shrink-0 flex-row items-center gap-2">
        {discount && (
          <span className="whitespace-nowrap font-medium text-success-300">
            {discount.discountAmount.amount} {discount.discountAmount.currency}
          </span>
        )}
        {row.kind === 'code' && (
          <button
            type="button"
            onClick={() => onRemove(row.code)}
            disabled={isRecalculating}
            className="text-neutral-400 transition hover:text-base-text disabled:opacity-50"
            aria-label={`Usuń kod ${row.code}`}
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Sekcja kodów promocyjnych na podsumowaniu rezerwacji (Krok 2).
 * Kody trzymane są w bookingStore - ich zmiana wyzwala przeliczenie quote.
 * Lista zbiera w jednym miejscu wpisane kody (z ich statusami) oraz rabaty
 * automatyczne; dopasowanie kodu do rabatu po `definition.promoCode`
 * (oba znormalizowane do UPPERCASE).
 */
const PromoCodeBookingSection = ({
  tourKey,
  appliedDiscounts,
  promoCodeStatuses,
  isRecalculating = false,
}: {
  /** Szkic, do którego należą kody — `bookingDraftKey(orgSlug, tourSlug)`. */
  tourKey: string;
  appliedDiscounts: AppliedDiscountDto[];
  promoCodeStatuses: PromoCodeStatusDto[];
  isRecalculating?: boolean;
}) => {
  const promoCodes = useBookingStore(
    (state) => draftOf(state, tourKey).promoCodes,
  );
  const setPromoCodes = useBookingStore((state) => state.setPromoCodes);

  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | undefined>();

  const rows = useMemo<DiscountRow[]>(() => {
    const codeRows: DiscountRow[] = promoCodes.map((code) => ({
      kind: 'code',
      code,
      status: promoCodeStatuses.find((s) => s.code === code)?.status,
      discount: appliedDiscounts.find((d) => d.definition.promoCode === code),
    }));
    const automaticRows: DiscountRow[] = appliedDiscounts
      .filter((d) => d.definition.promoCode == null)
      .map((discount) => ({ kind: 'automatic', discount }));
    return [...codeRows, ...automaticRows];
  }, [promoCodes, promoCodeStatuses, appliedDiscounts]);

  const hasNotAppliedCode =
    !isRecalculating &&
    rows.some(
      (r) =>
        r.kind === 'code' &&
        !r.discount &&
        r.status !== PromoCodeStatus.DEFERRED,
    );
  const hasDeferredCode =
    !isRecalculating &&
    rows.some(
      (r) => r.kind === 'code' && r.status === PromoCodeStatus.DEFERRED,
    );

  const handleApply = () => {
    const code = inputValue.trim().toUpperCase();
    if (code.length < MIN_CODE_LENGTH || code.length > MAX_CODE_LENGTH) {
      setError(
        `Kod musi mieć od ${MIN_CODE_LENGTH} do ${MAX_CODE_LENGTH} znaków`,
      );
      return;
    }
    if (promoCodes.includes(code)) {
      setError('Ten kod został już dodany');
      return;
    }
    setPromoCodes(tourKey, [...promoCodes, code]);
    setInputValue('');
    setError(undefined);
  };

  const handleRemove = (code: string) => {
    setPromoCodes(
      tourKey,
      promoCodes.filter((c) => c !== code),
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  return (
    <CardContent className="space-y-3">
      <div className="flex flex-row items-center gap-2">
        <p className="text-lg font-medium">Kod promocyjny</p>
        {isRecalculating && (
          <Loader className="size-4 animate-spin text-neutral-400" />
        )}
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex flex-row gap-2">
          <PrimitiveInput
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value.toUpperCase());
              setError(undefined);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Wpisz kod promocyjny"
            error={error}
          />
          <ActionButton
            type="button"
            tone="outline"
            size="sm"
            onClick={handleApply}
            disabled={isRecalculating}
          >
            Zastosuj
          </ActionButton>
        </div>
        <InputError error={error} />
      </div>

      {rows.length > 0 && (
        <div className="space-y-2">
          {rows.map((row) => (
            <DiscountRowItem
              key={
                row.kind === 'code'
                  ? `code-${row.code}`
                  : `auto-${row.discount.discountId}`
              }
              row={row}
              isRecalculating={isRecalculating}
              onRemove={handleRemove}
            />
          ))}
        </div>
      )}

      {hasDeferredCode && (
        <p className="text-xs text-neutral-400">
          Kod został przyjęty — rabat naliczy się automatycznie po uzupełnieniu
          brakujących danych formularza.
        </p>
      )}

      {hasNotAppliedCode && (
        <p className="text-xs text-neutral-400">
          Kod oznaczony jako niezastosowany jest nieaktywny, wygasł, nie dotyczy
          tej wycieczki lub nie spełnia warunków rezerwacji (np. minimalnej
          liczby uczestników albo kwoty).
        </p>
      )}
    </CardContent>
  );
};

export default PromoCodeBookingSection;
